import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import { branchesColumn } from "@ext/enterprise/components/admin/settings/members/config/branchesColumn";
import { nameColumn } from "@ext/enterprise/components/admin/settings/members/config/nameColumn";
import { roleColumn } from "@ext/enterprise/components/admin/settings/members/config/roleColumn";
import { userBadgesColumn } from "@ext/enterprise/components/admin/settings/members/config/userBadgesColumn";
import { userColumn } from "@ext/enterprise/components/admin/settings/members/config/userColumn";
import { useAccessDraft } from "@ext/enterprise/components/admin/settings/members/hooks/useAccessDraft";
import {
	getUserAccessRowId,
	type MemberAggregate,
	type RepoUserAccess,
	type UserMember,
} from "@ext/enterprise/components/admin/settings/members/model/Member";
import { limitRepositoryEditorRoles } from "@ext/enterprise/components/admin/settings/members/model/roleRules";
import { TypeFilterDropdown } from "@ext/enterprise/components/admin/settings/users/components/TypeFilterDropdown";
import { useUserList } from "@ext/enterprise/components/admin/settings/users/hooks/useUserList";
import type { ColumnDef, RowSelectionState } from "@ui-kit/DataTable";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import type { RoleId } from "../../enterprise/components/admin/settings/components/roles/Access";

export interface UseUserAccessPickerDialogContentArgs {
	repoId: string | undefined;
	aggregate: MemberAggregate;
	preselected: Set<string>;
	selection: RowSelectionState;
	selectedIds: string[];
	extraUsers: UserMember[];
	rowsMap: Map<string, RepoUserAccess>;
	setRowsMap: Dispatch<SetStateAction<Map<string, RepoUserAccess>>>;
	setSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

export const useUserAccessPickerDialogContent = (args: UseUserAccessPickerDialogContentArgs) => {
	const { repoId, aggregate, preselected, rowsMap, selection, selectedIds, extraUsers, setRowsMap, setSelection } =
		args;
	const { getBulkRepoUserRules, getUserRules, ssoUsersEnabled, searchBranches } = useSettings();

	const roleRules = useMemo(
		() => (repoId ? getUserRules() : getBulkRepoUserRules()),
		[repoId, getUserRules, getBulkRepoUserRules],
	);

	const onUsersLoad = useCallback(
		(xs: UserMember[]) => {
			setRowsMap((prev) => {
				const next = new Map(prev);
				xs.map((x) => {
					if (next.has(x.value)) return;
					next.set(x.value, {
						user: x,
						role: "reader",
					});
				});
				return next;
			});
		},
		[setRowsMap],
	);

	const userList = useUserList({ aggregate, onUsersLoad, enabled: true });

	const rows = useMemo(() => {
		const others = userList.data.users.filter((x) => !preselected.has(x.value));

		const rowUsers = [...extraUsers, ...others];
		return rowUsers.map<RepoUserAccess>((x) => rowsMap.get(x.value) ?? { user: x, role: "reader" });
	}, [userList.data.users, rowsMap, preselected, extraUsers]);

	const access = useAccessDraft({
		rowsMap,
		setRowsMap,
		roleRules,
		getId: getUserAccessRowId,
	});

	const columns = useMemo<ColumnDef<RepoUserAccess>[]>(
		() => [
			userColumn<RepoUserAccess>({
				getName: (row) => row.user.value,
			}),
			...(ssoUsersEnabled
				? [
						nameColumn<RepoUserAccess>({
							getName: (row) => row.user.name,
						}),
					]
				: []),
			userBadgesColumn({
				isEditor: (row) => row.user.isEditor,
				isWorkspaceOwner: (row) => row.user.isWorkspaceOwner,
			}),
			roleColumn<RepoUserAccess>({
				getRules: (row) => limitRepositoryEditorRoles(roleRules, row.user.isEditor),
				getValue: (row) => row.role,
				onChange: (row, role) => access.setRole([row.user.value], role),
				isDisabled: (row) => !selection[row.user.value],
			}),
			branchesColumn<RepoUserAccess>({
				getValue: (row) => row.branches,
				showPicker: (row) => (row.role === "reviewer" ? (!repoId ? "disabled" : true) : false),
				onChange: (row, branches) => access.setBranches(row.user.value, branches, row.role),
				loadBranches: (_row, params) => searchBranches(repoId, params),
				getError: (row) => access.branchErrors?.get(row.user.value),
			}),
		],
		[
			access.branchErrors,
			access.setBranches,
			access.setRole,
			roleRules,
			searchBranches,
			selection,
			repoId,
			ssoUsersEnabled,
		],
	);

	const createNew = useCallback(
		(id: string) => {
			const newRow: RepoUserAccess = {
				user: {
					value: id,
					isEditor: false,
					isWorkspaceOwner: false,
				},
				role: "reader",
			};

			setSelection((prev) => ({ ...prev, [id]: true }));
			setRowsMap((prev) => {
				const next = new Map(prev);
				next.set(newRow.user.value, newRow);
				return next;
			});
		},
		[setRowsMap, setSelection],
	);

	const selectedRoleRules = useMemo(
		() =>
			limitRepositoryEditorRoles(
				roleRules,
				selectedIds.every((id) => rowsMap.get(id)?.user.isEditor),
			),
		[roleRules, selectedIds, rowsMap],
	);

	const bulkRoleChange = useCallback(
		(role: RoleId) => {
			access.setRole(selectedIds, role);
		},
		[access.setRole, selectedIds],
	);

	const headerLeftControls = (
		<TypeFilterDropdown onSelectType={userList.filter.type.set} selectedType={userList.filter.type.selected} />
	);

	return {
		data: {
			rows,
			rowVersions: access.rowVersions,
			columns,
			isLoading: userList.data.isLoading,
			getRowId: getUserAccessRowId,
			filter: userList.filter,
			ssoEnabled: ssoUsersEnabled,
			roleRules: selectedRoleRules,
		},
		headerLeftControls,
		actions: {
			createNew,
			bulkRoleChange,
		},
	};
};
